import { useCallback, useEffect, useMemo, useState } from 'react'
import { getId } from '@fluentui/react'

const getWidth = () => document.body.clientWidth
const getHeight = () => document.body.clientHeight

type ViewportProps = {
    width: number
    height: number
    viewport: Viewport
    isViewport: (operator: '>' | '<' | '<=' | '>=' | '==' | '=' | '!=', target: Viewport) => boolean
    getGap: (breakpoint?: Viewport, small?: string, large?: string) => string
}

//All breakpoints must be sorted based on their pixel size.
export enum Viewport {
    Small = 'SMALL',
    OutlookWeb = 'OUTLOOK_WEB',
    Medium = 'MEDIUM',
    OutlookNew = 'OUTLOOK_NEW',
    Large = 'LARGE',
    MainMenu = 'MAINMENU',
    XL = 'XL',
    XXL = 'XXL',
    Max = 'MAXIMUM',
}

//All breakpoints must be sorted based on their pixel size.
export const BreakPointSizeMap = new Map<Viewport, number>([
    [Viewport.Small, 200],
    [Viewport.OutlookWeb, 315], // 319 for <iframe>, 304 for <body>.
    [Viewport.Medium, 360],
    [Viewport.OutlookNew, 386], // 400 for <iframe>, 385 for <body>.
    [Viewport.Large, 450],
    [Viewport.MainMenu, 550],
    [Viewport.XL, 720],
    [Viewport.XXL, 960],
    [Viewport.Max, 1280],
])

const timeouts = new Map<string, NodeJS.Timeout>()

const removeTimeout = (id: string) => {
    if (timeouts.has(id)) {
        clearTimeout(timeouts.get(id))
        timeouts.delete(id)
    }
}

const compareViewports = (target: Viewport, current: Viewport, viewports: Viewport[]): boolean => {
    for (const candidate of viewports) {
        if (candidate === target) {
            return false
        }
        if (candidate === current) {
            break
        }
    }
    return true
}

export default function useViewport(): ViewportProps {
    // save current window width in the state object
    const [width, setWidth] = useState<number>(getWidth())
    const [height, setHeight] = useState<number>(getHeight())
    const [id] = useState<string>(getId())
    const viewport: Viewport = useMemo(() => {
        for (const candidate of Object.values(Viewport).reverse()) {
            if (width > BreakPointSizeMap.get(candidate)) {
                return candidate
            }
        }
        return Viewport.Small
    }, [width])
    const getGap = useCallback(
        // small = 8px or spacingHorizontalS, large = 16px or spacingHorizontalL
        (breakpoint: Viewport = Viewport.Medium, small: string = 's1', large: string = 'm') => (isViewport('<=', breakpoint) ? small : large),
        [viewport],
    )

    const resizeListener = () => {
        removeTimeout(id)
        timeouts.set(
            id,
            setTimeout(() => {
                setWidth(getWidth())
                setHeight(getHeight())
            }, 100),
        )
    }

    const isViewport = useCallback(
        (operator: '>' | '<' | '<=' | '>=' | '==' | '=' | '!=', target: Viewport): boolean => {
            if (['<=', '==', '=', '>='].includes(operator) && viewport === target) {
                return true
            }
            switch (operator) {
                case '>':
                case '>=':
                    return compareViewports(target, viewport, Object.values(Viewport).reverse())
                case '<':
                case '<=':
                    return compareViewports(target, viewport, Object.values(Viewport))
                case '!=':
                    return viewport !== target
                default:
                    return false
            }
        },
        [viewport],
    )

    useEffect(() => {
        window.addEventListener('resize', resizeListener)
        return () => {
            removeTimeout(id)
            window.removeEventListener('resize', resizeListener)
        }
    }, [])

    return { width, height, viewport, isViewport, getGap }
}
